/**
 * Browser-gesture guard (view layer only — never touches the simulation).
 *
 * The viewport meta tag + CSS `touch-action: none` stop most native zoom/scroll,
 * but two cases need JS:
 *   1. iOS Safari fires its own non-standard pinch events (gesturestart /
 *      gesturechange / gestureend) and will page-zoom even with
 *      user-scalable=no — so we preventDefault them.
 *   2. Double-tap-to-zoom: belt-and-braces on top of touch-action, with the
 *      game's own GROW/SHRINK buttons excluded so rapid double-taps on them are
 *      never swallowed.
 *
 * The game's own pinch-to-zoom (ZoomInput) uses ordinary touch events and is
 * unaffected: Safari's gesture* events are a separate channel from touch events.
 */
const DOUBLE_TAP_MS = 300;

/** Install the guards once. Safe to call on any device (no-ops where the events
 *  never fire, e.g. desktop without a pinch-capable trackpad). */
export function installGestureGuard(): void {
  const preventDefault = (e: Event): void => {
    e.preventDefault();
  };

  // 1) iOS Safari pinch gestures.
  document.addEventListener('gesturestart', preventDefault, { passive: false });
  document.addEventListener('gesturechange', preventDefault, { passive: false });
  document.addEventListener('gestureend', preventDefault, { passive: false });

  // 2) Double-tap-to-zoom — but leave the game's touch controls alone so rapid
  //    tapping of the GROW button is never blocked.
  let lastTouchEnd = 0;
  document.addEventListener(
    'touchend',
    (e: TouchEvent) => {
      const now = performance.now();
      const target = e.target;
      // Leave the game's own tap UI alone (GROW/SHRINK buttons + the leaderboard
      // collapse toggle) so their taps are never swallowed; their own
      // touch-action:none already blocks zoom there.
      const inUi = target instanceof Element && target.closest('#touch-controls, #leaderboard');
      if (!inUi && now - lastTouchEnd <= DOUBLE_TAP_MS) e.preventDefault();
      lastTouchEnd = now;
    },
    { passive: false },
  );
}
